import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Ruler, Truck, MapPin, ShieldCheck, Heart } from "lucide-react";
import { http, label } from "../lib/api";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";

const PATTERNS = ["visgraat", "walvisgraat", "rechte-stroken", "hongaarse-punt", "tegel"];
const ORG = { "@context": "https://schema.org", "@graph": [
  { "@type": "Organization", name: "VLOVER by Next Level", url: "https://www.vlover.nl", logo: "https://www.vlover.nl/nl-monogram.png", address: { "@type": "PostalAddress", streetAddress: "Jonge Voolweg 29", postalCode: "1521 RH", addressLocality: "Wormerveer", addressCountry: "NL" } },
  { "@type": "WebSite", name: "VLOVER by Next Level", url: "https://www.vlover.nl", potentialAction: { "@type": "SearchAction", target: "https://www.vlover.nl/zoeken?q={search_term_string}", "query-input": "required name=search_term_string" } }] };
const amb = (a, w = 1400) => (a ? `${a.src}?width=${w}` : "");

function Hero({ a }) {
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-6 sm:pt-10" data-testid="home-hero">
      <div className="grid lg:grid-cols-[1fr_1.1fr] gap-6 items-stretch">
        <div className="bg-[#F2E6E0] rounded-3xl p-8 sm:p-12 lg:p-14 flex flex-col justify-center fade-up">
          <p className="eyebrow mb-5">Welkom bij VLOVER · by Next Level</p>
          <h1 className="font-serif-brand text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#34393E]">Vind de vloer die echt <em className="text-[#B78B7B]">thuis</em> voelt.</h1>
          <p className="mt-6 max-w-md text-base text-[#5A5550] leading-relaxed">Warme houtlooks, sierlijke visgraat of een rustige tegel: we helpen je kiezen, rekenen precies uit hoeveel pakken je nodig hebt en bezorgen het netjes bij je thuis.</p>
          <div className="flex flex-wrap gap-3 mt-9">
            <Link to="/pvc" data-testid="hero-cta-pvc" className="bg-[#34393E] hover:bg-[#B78B7B] text-white px-7 py-3.5 rounded-xl text-sm font-medium transition-colors inline-flex items-center gap-2">Ontdek PVC-vloeren <ArrowRight size={16} /></Link>
            <Link to="/laminaat" data-testid="hero-cta-laminaat" className="bg-white hover:bg-[#F6F3EE] px-7 py-3.5 rounded-xl text-sm font-medium transition-colors">Bekijk laminaat</Link>
          </div>
        </div>
        <div className="grid grid-cols-5 grid-rows-2 gap-4 min-h-[360px] lg:min-h-[560px]">
          <div className="col-span-5 sm:col-span-3 row-span-2 rounded-3xl overflow-hidden bg-[#EFE8E0]">{a[0] && <img src={amb(a[0])} alt={`Sfeerbeeld ${a[0].name}`} className="w-full h-full object-cover" data-testid="hero-image-main" />}</div>
          <div className="hidden sm:block col-span-2 rounded-3xl overflow-hidden bg-[#EFE8E0]">{a[1] && <img src={amb(a[1], 800)} alt={`Sfeerbeeld ${a[1].name}`} className="w-full h-full object-cover" />}</div>
          <div className="hidden sm:flex col-span-2 rounded-3xl bg-[#34393E] text-white p-6 lg:p-8 flex-col justify-center">
            <Heart size={20} className="text-[#B78B7B]" /><p className="font-serif-brand text-2xl mt-3 leading-snug">Persoonlijk advies uit Wormerveer</p><p className="text-xs text-white/70 mt-2">Kom langs, bel of mail. We denken graag met je mee.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Usps() {
  const items = [[Truck, "Gratis bezorgd vanaf €750"], [MapPin, "Gratis afhalen in Wormerveer"], [Ruler, "m²-rekenhulp met snijverlies"], [ShieldCheck, "Veilig betalen via Mollie"]];
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 mt-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="home-usps">
        {items.map(([I, t]) => <div key={t} className="bg-[#EFE8E0] rounded-2xl px-5 py-4 flex items-center gap-3 text-sm text-[#34393E]"><I size={18} className="text-[#B78B7B] shrink-0" />{t}</div>)}
      </div>
    </section>
  );
}

function Categories({ site, a }) {
  const cats = [["pvc", "PVC-vloeren", "Plak, click en loose lay. Stil, warm en waterbestendig.", a.filter((x) => x.group === "pvc")[2]],
    ["laminaat", "Laminaat", "Stoere houtlooks en visgraat, sterk en betaalbaar.", a.filter((x) => x.group === "laminaat")[3]]];
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-20 grid md:grid-cols-2 gap-6">
      {cats.map(([to, t, d, img]) => (
        <Link key={to} to={`/${to}`} data-testid={`home-category-${to}`} className="relative h-[420px] rounded-3xl overflow-hidden img-zoom group">
          {img && <img src={amb(img, 1200)} alt={t} className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#24282B]/70 via-transparent to-transparent" />
          <div className="absolute bottom-5 left-5 right-5 bg-[#F6F3EE]/95 backdrop-blur rounded-2xl p-6 flex items-end justify-between gap-4">
            <div><p className="text-xs text-[#B78B7B] font-semibold">{site?.groups?.[to] ?? "…"} vloeren</p><h2 className="font-serif-brand text-3xl mt-1 text-[#34393E]">{t}</h2><p className="text-sm text-[#5A5550] mt-1">{d}</p></div>
            <span className="w-11 h-11 rounded-full bg-[#34393E] group-hover:bg-[#B78B7B] text-white flex items-center justify-center shrink-0 transition-colors"><ArrowRight size={18} /></span>
          </div>
        </Link>
      ))}
    </section>
  );
}

function Patterns({ thumbs }) {
  return (
    <section className="bg-[#F2E6E0]">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <p className="eyebrow">Kies je legpatroon</p>
        <h2 className="font-serif-brand text-4xl mt-2 text-[#34393E]">Welke stijl past bij jou?</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-10">
          {PATTERNS.map((k) => (
            <Link key={k} to={`/pvc?pattern=${k}`} data-testid={`pattern-chip-${k}`} className="group bg-white rounded-2xl p-2.5 lift">
              <div className="aspect-square rounded-xl overflow-hidden bg-[#EFE8E0]">{thumbs[k] && <img src={thumbs[k]} alt={label[k]} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}</div>
              <p className="text-sm font-medium text-center py-3 group-hover:text-[#B78B7B] transition-colors">{label[k]}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function Moodboard({ a }) {
  if (a.length < 9) return null;
  const used = new Set([a.filter((x) => x.group === "pvc")[2]?.src, a.filter((x) => x.group === "laminaat")[3]?.src]);
  const pics = a.slice(2).filter((x) => !used.has(x.src)).slice(0, 7);
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-20" data-testid="home-moodboard">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div><p className="eyebrow">Inspiratie</p><h2 className="font-serif-brand text-4xl mt-2 text-[#34393E]">Zo staat het bij je thuis</h2></div>
        <p className="text-sm text-[#6E6862] max-w-sm">Sfeerbeelden van onze leveranciers. Klik op een foto om de vloer te bekijken.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 auto-rows-[180px] sm:auto-rows-[220px] gap-4">
        {pics.map((x, i) => (
          <Link key={x.src} to={`/product/${x.slug}`} data-testid={`moodboard-item-${i}`} className={`relative rounded-3xl overflow-hidden img-zoom group ${i === 0 ? "col-span-2 row-span-2" : i >= 5 ? "lg:col-span-2" : ""}`}>
            <img src={amb(x, i === 0 ? 1200 : 700)} alt={`Sfeerbeeld ${x.name}`} loading="lazy" className="w-full h-full object-cover" />
            <span className="absolute bottom-3 left-3 right-3 bg-white/90 backdrop-blur rounded-full px-4 py-1.5 text-xs truncate opacity-0 group-hover:opacity-100 transition-opacity">{x.brand} · {x.name}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const [site, setSite] = useState(null);
  const [items, setItems] = useState([]);
  const [thumbs, setThumbs] = useState({});
  useEffect(() => {
    http.get("/site").then((r) => setSite(r.data));
    http.get("/products", { params: { pattern: "visgraat,walvisgraat" } }).then((r) => setItems(r.data.items.slice(0, 8)));
    PATTERNS.forEach((k) => http.get("/products", { params: { pattern: k } }).then((r) => { const x = r.data.items.find((p) => p.image); if (x) setThumbs((t) => ({ ...t, [k]: x.image.src })); }));
  }, []);
  const a = site?.ambiance || [];
  return (
    <div className="pb-10">
      <Seo title="PVC- en laminaatvloeren" jsonld={ORG} description="VLOVER by Next Level: PVC- en laminaatvloeren met duidelijke prijzen per m² en per pak, persoonlijk advies en eerlijke levertijden." />
      <Hero a={a} />
      <Usps />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 mt-6"><Link to="/keuzehulp-vloeropbouw" data-testid="home-floor-guide" className="bg-[#272D30] text-white rounded-2xl px-6 py-5 flex items-center justify-between gap-4 hover:bg-[#34393E] transition-colors"><span><span className="block text-sm font-medium">Welke egaline, ondervloer en lijm heb je nodig?</span><span className="block text-xs text-white/65 mt-1">Vind mogelijke producten met onze korte keuzehulp — inclusief controlepunten.</span></span><ArrowRight size={20} className="shrink-0 text-[#D9A48C]" /></Link></div>
      <Categories site={site} a={a} />
      <Patterns thumbs={thumbs} />
      <Moodboard a={a} />
      {items.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pb-10">
          <div className="flex items-end justify-between mb-10">
            <div><p className="eyebrow mb-2">Favorieten</p><h2 className="font-serif-brand text-4xl text-[#34393E]">Geliefd: visgraat & walvisgraat</h2></div>
            <Link to="/pvc?pattern=visgraat" className="text-sm hover:text-[#B78B7B] inline-flex items-center gap-1" data-testid="home-favorites-all">Alles bekijken <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">{items.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}</div>
        </section>
      )}
    </div>
  );
}
